import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

// Custom Apple-esque Tooltip Component
const MilestoneTooltip = ({ active, payload, label }) => {
  if (!active || !payload || payload.length === 0) {
    return null;
  }

  const data = payload[0].payload;
  
  return (
    <div className="milestone-tooltip">
      <div className="tooltip-content">
        <div className="tooltip-header" style={{ '--accent-color': data.color }}>
          <div className="tooltip-accent-bar"></div>
          <h4 className="tooltip-title">{data.name}</h4>
        </div>
        
        <div className="tooltip-body">
          <div className="tooltip-goal">
            <span className="tooltip-label">Goal:</span>
            <span className="tooltip-value">{data.goalTitle}</span>
          </div>
          
          <div className="tooltip-meta">
            <div className="tooltip-meta-item">
              <span className="tooltip-label">Timeline:</span>
              <span className="tooltip-value">Month {Math.round(data.time * 10) / 10}</span>
            </div>
            <div className="tooltip-meta-item">
              <span className="tooltip-label">Progress:</span>
              <span className="tooltip-value">{Math.round(data.level * 10) / 10}/10</span>
            </div>
            {data.importance && (
              <div className="tooltip-meta-item">
                <span className="tooltip-label">Priority:</span>
                <span className="tooltip-value importance-value">
                  {(data.importance/10).toFixed(1)}/10
                </span>
              </div>
            )}
          </div>
          
          {data.description && (
            <div className="tooltip-description">
              <p>{data.description}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Custom Legend Component
const CustomLegend = ({ goalGroups }) => {
  return (
    <div style={{
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'center',
      gap: '16px',
      marginTop: '24px',
      padding: '16px',
      backgroundColor: '#f8f9fa',
      borderRadius: '12px',
      border: '1px solid rgba(0, 0, 0, 0.06)',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
    }}>
      {Object.entries(goalGroups).map(([goalId, data]) => (
        <div
          key={goalId}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            backgroundColor: 'white',
            padding: '8px 12px',
            borderRadius: '20px',
            border: '1px solid rgba(0, 0, 0, 0.08)',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.04)',
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            cursor: 'default'
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'translateY(-1px)';
            e.target.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.08)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'translateY(0px)';
            e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.04)';
          }}
        >
          <div
            style={{
              width: '12px',
              height: '12px',
              borderRadius: '50%',
              backgroundColor: data[0].color,
              border: '2px solid white',
              boxShadow: '0 0 0 1px rgba(0, 0, 0, 0.1)'
            }}
          />
          <span
            style={{
              fontSize: '14px',
              fontWeight: '500',
              color: '#333',
              lineHeight: '1.2',
              maxWidth: '200px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {goalId}
          </span>
        </div>
      ))}
    </div>
  );
};

const GoalTimelineChart = ({ userGoals, parseAiSuggestions, type = "monotone", onMilestoneClick }) => {

  // Cooler color palette for milestones - different shades
  const coolMilestoneColors = [
    '#3B82F6', // Blue
    '#06B6D4', // Cyan
    '#10B981', // Emerald
    '#8B5CF6', // Violet
    '#6366F1', // Indigo
    '#0EA5E9', // Sky
    '#14B8A6', // Teal
    '#8B5CF6', // Purple
    '#6366F1', // Indigo Blue
    '#2563EB', // Blue-600
    '#0891B2', // Cyan-600
    '#059669', // Emerald-600
    '#7C3AED', // Violet-600
    '#4F46E5', // Indigo-600
    '#0284C7', // Sky-600
    '#0D9488', // Teal-600
    '#1E40AF', // Blue-700
    '#0E7490', // Cyan-700
    '#047857', // Emerald-700
    '#6D28D9'  // Violet-700
  ];

  // Function to get milestone color based on goal index and milestone index
  const getMilestoneColor = (goalIndex, milestoneIndex) => {
    // Create a unique index by combining goal and milestone indices
    const colorIndex = (goalIndex * 7 + milestoneIndex) % coolMilestoneColors.length;
    return coolMilestoneColors[colorIndex];
  };

  // Function to convert userGoals data into format needed for Recharts
  const formatGoalData = () => {
    // Create an array to hold all chart data
    const chartData = [];
    
    userGoals.forEach((goal, goalIndex) => {
      // Create base goal points (start and end)
      // Handle various date formats
      let dueDate;
      try {
        // Try to parse the date - could be in different formats
        const dateString = goal.dueDate;
        // Check if format is MM/DD/YYYY
        if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateString)) {
          const [month, day, year] = dateString.split('/').map(Number);
          dueDate = new Date(year, month - 1, day); // Month is 0-indexed in JavaScript
        } else {
          dueDate = new Date(dateString);
        }
        
        if (isNaN(dueDate.getTime())) {
          throw new Error('Invalid date');
        }
      } catch (error) {
        console.error("Error parsing date:", goal.dueDate, error);
        // Set a default date 3 months in the future
        dueDate = new Date();
        dueDate.setMonth(dueDate.getMonth() + 3);
      }
      
      const today = new Date();
      
      // Calculate months difference for x-axis
      const monthsDiff = (dueDate.getFullYear() - today.getFullYear()) * 12 + 
                         (dueDate.getMonth() - today.getMonth());
      
      // Add starting point (today at level 0)
      chartData.push({
        goalId: goal.title,
        name: 'Start',
        time: 0,
        level: 0,
        description: 'Beginning of goal',
        color: goal.color,
        goalTitle: goal.title,
        goalIndex: goalIndex,
        milestoneIndex: -1, // -1 for start point
        isClickable: false
      });
      
      // Add due date point (target date at level 10)
      chartData.push({
        goalId: goal.title,
        name: 'Target Completion',
        time: Math.max(1, monthsDiff), // Ensure at least 1 month
        level: 10,
        description: `Due: ${goal.dueDate}`,
        color: goal.color,
        goalTitle: goal.title,
        goalIndex: goalIndex,
        milestoneIndex: -2, // -2 for end point
        isClickable: false
      });
      
      // Add milestone points if available
      const suggestions = parseAiSuggestions(goal.aiSuggestions);
      console.log(`Processing milestones for goal "${goal.title}":`, suggestions); // Debug log
      
      // Debug the structure more thoroughly
      if (suggestions) {
        console.log(`Suggestions structure for "${goal.title}":`, {
          hasData: !!suggestions.data,
          dataKeys: suggestions.data ? Object.keys(suggestions.data) : 'no data',
          hasRecommendations: suggestions.data && !!suggestions.data.recommendations,
          recommendationsLength: suggestions.data && suggestions.data.recommendations ? suggestions.data.recommendations.length : 0,
          firstRecommendation: suggestions.data && suggestions.data.recommendations && suggestions.data.recommendations[0] ? suggestions.data.recommendations[0] : 'no first rec',
          hasPath1: suggestions.data && suggestions.data.recommendations && suggestions.data.recommendations[0] ? !!suggestions.data.recommendations[0].path1 : false,
          path1Keys: suggestions.data && suggestions.data.recommendations && suggestions.data.recommendations[0] && suggestions.data.recommendations[0].path1 ? Object.keys(suggestions.data.recommendations[0].path1) : 'no path1'
        });
      }
      
      if (suggestions && suggestions.data && suggestions.data.recommendations && 
          suggestions.data.recommendations[0] && suggestions.data.recommendations[0].path1) {
        
        const milestones = suggestions.data.recommendations[0].path1;
        console.log(`Milestone data shape for "${goal.title}":`, milestones); // Debug log
        
        // Convert object to array if needed and ensure proper structure
        let milestoneArray;
        if (Array.isArray(milestones)) {
          milestoneArray = milestones;
        } else if (typeof milestones === 'object' && milestones !== null) {
          // Convert object with step1, step2, etc. to array
          milestoneArray = Object.values(milestones);
        } else {
          console.warn(`Invalid milestone data structure for goal "${goal.title}":`, milestones);
          milestoneArray = [];
        }
        
        console.log(`Processed milestone array for "${goal.title}":`, milestoneArray); // Debug log
        
        const totalSteps = milestoneArray.length;
        
        milestoneArray.forEach((step, index) => {
          // Ensure step has required fields
          if (!step || typeof step !== 'object') {
            console.warn(`Invalid step data at index ${index} for goal "${goal.title}":`, step);
            return;
          }
          
          // Calculate position along timeline
          const stepPosition = (index + 1) / (totalSteps + 1);
          const timeValue = stepPosition * Math.max(1, monthsDiff);
          const levelValue = stepPosition * 10; // Progress from 0 to 10
          
          chartData.push({
            goalId: goal.title,
            name: step.title || `Step ${index + 1}`,
            time: timeValue,
            level: levelValue,
            description: step.details || step.description || '',
            color: goal.color, // Use goal color to match the line
            importance: step.importance || 10,
            goalTitle: goal.title,
            goalIndex: goalIndex,
            milestoneIndex: index,
            milestoneKey: `step${index + 1}`,
            isMilestone: true,
            isClickable: true
          });
        });
      } else {
        console.log(`No milestone data found for goal "${goal.title}"`); // Debug log
      }
    });
    
    return chartData;
  };
  
  // Handle dot click
  const handleDotClick = (data, event) => {
    if (data && data.isClickable && onMilestoneClick) {
      event.stopPropagation();
      onMilestoneClick(data.goalIndex, data.milestoneIndex, data.milestoneKey);
    }
  };

  // Process data for the chart
  const chartData = formatGoalData();
  
  console.log("Chart data:", chartData); // Debug log
  
  // Group data by goalId to create separate lines
  const goalGroups = chartData.reduce((groups, item) => {
    if (!groups[item.goalId]) {
      groups[item.goalId] = [];
    }
    groups[item.goalId].push(item);
    return groups;
  }, {});
  
  console.log("Goal groups:", goalGroups); // Debug log
  
  // Sort each group by time
  Object.keys(goalGroups).forEach(key => {
    goalGroups[key].sort((a, b) => a.time - b.time);
  });
  
  // Calculate domain limits
  const calculateDomain = () => {
    if (chartData.length === 0) return [0, 12];
    
    const maxTime = Math.max(...chartData.map(item => item.time));
    // Add some padding and round up to nearest 3 months
    return [0, Math.ceil(maxTime * 1.2 / 3) * 3];
  };
  
  const timeDomain = calculateDomain();
  
  // If no data points, show empty message
  if (Object.keys(goalGroups).length === 0) {
    return (
      <div style={{ textAlign: 'center', color: '#666', padding: '40px' }}>
        Add goals with future dates to see your personalized timeline visualization.
      </div>
    );
  }
  
  return (
    <div style={{ width: '100%', height: '500px', display: 'flex', flexDirection: 'column' }}>
      <div style={{ width: '100%', height: '400px' }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart 
            margin={{ top: 20, right: 30, left: 20, bottom: 30 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              type="number"
              dataKey="time" 
              domain={timeDomain}
              allowDecimals={false}
              label={{ value: 'Months', position: 'insideBottomRight', offset: -5 }}
            />
            <YAxis 
              domain={[0, 10]}
              label={{ value: 'Progress', angle: -90, position: 'insideLeft' }}
            />
            <Tooltip 
              content={<MilestoneTooltip />}
              wrapperStyle={{ 
                outline: 'none',
                pointerEvents: 'none'
              }}
              cursor={{
                stroke: 'rgba(74, 108, 247, 0.3)',
                strokeWidth: 2,
                strokeDasharray: '5,5'
              }}
              animationEasing="ease-out"
              animationDuration={200}
            />
            
            {/* Create a line for each goal */}
            {Object.entries(goalGroups).map(([goalId, data]) => (
              <Line 
                key={goalId}
                type={type} 
                data={data}
                dataKey="level" 
                name={goalId}
                stroke={data[0].color} 
                strokeWidth={3}
                dot={(props) => {
                  const { cx, cy, payload } = props;
                  
                  // Different styling for milestones vs. endpoints
                  const radius = payload.isMilestone ? 6 : 8;
                  const strokeWidth = payload.isMilestone ? 2 : 3;
                  const isClickable = payload.isClickable;
                  
                  return (
                    <circle 
                      cx={cx} 
                      cy={cy} 
                      r={radius}
                      fill={payload.color}
                      stroke="#fff" 
                      strokeWidth={strokeWidth}
                      style={{ 
                        cursor: isClickable ? 'pointer' : 'default',
                        filter: isClickable ? 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2))' : 'none'
                      }}
                      onClick={(event) => handleDotClick(payload, event)}
                      className={isClickable ? 'clickable-milestone' : ''}
                    />
                  );
                }}
                activeDot={{ r: 8, fill: 'white', stroke: data[0].color, strokeWidth: 3 }}
                connectNulls={true}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
      
      {/* Custom Legend */}
      <CustomLegend goalGroups={goalGroups} />
    </div>
  );
};

export default GoalTimelineChart; 